import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Chip } from "@/components/ui/chip";
import { Skeleton } from "@/components/ui/skeleton";
import { Timestamp } from "@/components/ui/timestamp";
import { useCanEdit } from "@/lib/project-access";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { toast } from "sonner";
import { Plus, Lightbulb } from "lucide-react";
import {
  KIND_LABEL,
  type GuidanceKind,
  type GuidanceRule,
  AddGuidanceRuleDialog,
  EditGuidanceRuleDialog,
} from "./guidance-rule-dialogs";

export interface GuidanceSectionProps {
  projectId: string;
  rules?: GuidanceRule[];
  isLoading?: boolean;
}

export function GuidanceSection({
  projectId,
  rules: initialRules,
  isLoading: isInitialLoading,
}: GuidanceSectionProps) {
  const utils = trpc.useUtils();
  const { data: fetchedRules, isLoading: isQueryLoading } =
    trpc.guidance.list.useQuery(
      { projectId, status: "all" },
      { enabled: !initialRules },
    );
  const rules = initialRules ?? (fetchedRules as GuidanceRule[] | undefined);
  const isLoading = isInitialLoading ?? isQueryLoading;

  const { data: capabilities } = trpc.capability.list.useQuery({ projectId });
  const { data: outlines } = trpc.outline.list.useQuery({ projectId });
  const canEdit = useCanEdit();

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<GuidanceRule | null>(null);
  const [showRetired, setShowRetired] = useState(false);

  const setStatus = trpc.guidance.setStatus.useMutation({
    onSuccess: (row) => {
      utils.guidance.list.invalidate({ projectId });
      toast.success(
        row.status === "retired"
          ? "Runner rule retired"
          : "Runner rule restored",
      );
    },
    onError: (err) =>
      toastTrpcError(err, "Failed to update runner rule status"),
  });

  const scopeLabel = (scope: GuidanceRule["scope"]): string => {
    if (scope?.capabilityId) {
      const c = capabilities?.find((x) => x.id === scope.capabilityId);
      return c ? `Area: ${c.name}` : "One product area";
    }
    if (scope?.outlineId) {
      const o = outlines?.find((x) => x.id === scope.outlineId);
      return o ? `Scenario: ${o.name}` : "One scenario";
    }
    return "Whole project";
  };

  const all = (rules ?? []) as GuidanceRule[];
  const active = all.filter((r) => r.status === "active");
  const retired = all.filter((r) => r.status === "retired");

  const renderRuleList = (list: GuidanceRule[], isRetiredList = false) => (
    <ul className="divide-y divide-border/60">
      {list.map((r) => (
        <li
          key={r.id}
          className={`flex items-start justify-between gap-4 py-3 ${
            isRetiredList ? "opacity-75" : ""
          }`}
        >
          <div className="min-w-0 flex-1 space-y-1.5">
            <p className="text-sm font-semibold leading-snug">{r.rule}</p>
            {r.reason && (
              <p className="text-xs text-muted-foreground leading-relaxed">
                {r.reason}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2 pt-1 text-xs text-muted-foreground">
              <Chip
                tone={
                  r.kind === "out_of_scope"
                    ? "primary"
                    : r.kind === "walker_error"
                      ? "important"
                      : "neutral"
                }
              >
                {KIND_LABEL[r.kind as GuidanceKind] ?? r.kind}
              </Chip>
              <Chip tone="outline">{scopeLabel(r.scope)}</Chip>
              {r.sourceCanonicalIds.length > 0 && (
                <span className="tabular-nums inline-flex items-center gap-1">
                  <span>From</span>
                  {r.sourceCanonicalIds.map((cid, idx) => (
                    <Link
                      key={cid}
                      to={`/projects/${projectId}/findings/${cid}`}
                      className="underline font-medium hover:text-foreground inline-flex items-center"
                    >
                      finding
                      {r.sourceCanonicalIds.length > 1 ? ` #${idx + 1}` : ""}
                    </Link>
                  ))}
                </span>
              )}
              <Timestamp date={r.createdAt} className="tabular-nums" />
            </div>
          </div>
          {canEdit && (
            <div className="flex shrink-0 items-center gap-2">
              {!isRetiredList && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditingRule(r)}
                >
                  Edit
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                disabled={setStatus.isPending}
                onClick={() =>
                  setStatus.mutate({
                    projectId,
                    id: r.id,
                    status: r.status === "active" ? "retired" : "active",
                  })
                }
              >
                {r.status === "active" ? "Retire" : "Restore"}
              </Button>
            </div>
          )}
        </li>
      ))}
    </ul>
  );

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div className="space-y-1.5">
          <CardTitle className="text-base">
            Learned guardrails & exclusions
          </CardTitle>
          <CardDescription>
            Negative rules learned from triage dismissals (out of scope, not
            relevant, runner error). Walkthrough runners follow these to avoid
            repeating unwanted findings.
          </CardDescription>
        </div>
        {canEdit && (
          <Button
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={() => setIsAddOpen(true)}
          >
            <Plus className="mr-1 h-3.5 w-3.5" />
            Add rule
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : all.length === 0 ? (
          <div className="py-6 text-center">
            <Lightbulb className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
            <p className="text-sm font-medium text-foreground">
              No learned guardrails yet
            </p>
            <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
              When you dismiss findings during triage with &quot;Teach
              runner&quot;, rules appear here and instruct future walkthroughs.
            </p>
            {canEdit && (
              <Button
                variant="outline"
                size="sm"
                className="mt-3 text-xs"
                onClick={() => setIsAddOpen(true)}
              >
                <Plus className="mr-1 h-3.5 w-3.5" />
                Add rule manually
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              {active.length > 0 ? (
                renderRuleList(active)
              ) : (
                <p className="py-4 text-sm text-muted-foreground">
                  No active rules. All rules are currently retired.
                </p>
              )}
            </div>

            {retired.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-border/50">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setShowRetired((prev) => !prev)}
                >
                  {showRetired ? "Hide" : "Show"} retired rules (
                  {retired.length})
                </Button>
                {showRetired && <div>{renderRuleList(retired, true)}</div>}
              </div>
            )}
          </div>
        )}
      </CardContent>

      {canEdit && isAddOpen && (
        <AddGuidanceRuleDialog
          projectId={projectId}
          onClose={() => setIsAddOpen(false)}
        />
      )}

      {canEdit && editingRule && (
        <EditGuidanceRuleDialog
          projectId={projectId}
          rule={editingRule}
          onClose={() => setEditingRule(null)}
        />
      )}
    </Card>
  );
}
